'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';

import { getRandomQuestions } from './data/careerQuizQuestions';
import type { QuizQuestion, QuizState } from './types';
import './career-quiz.css';

export function CareerQuiz404({ missingRoute }: { missingRoute?: string }) {
  const [quizState, setQuizState] = useState<QuizState>({
    questions: [],
    currentIndex: 0,
    selectedOption: null,
    isAnswered: false,
    score: 0,
    userAnswers: [],
    isComplete: false,
  });

  const [mounted, setMounted] = useState(false);

  // Initialize 10 random questions on mount with option shuffling
  useEffect(() => {
    setQuizState((prev) => ({
      ...prev,
      questions: getRandomQuestions(10),
    }));
    setMounted(true);
  }, []);

  const handleStartOver = () => {
    setQuizState({
      questions: getRandomQuestions(10),
      currentIndex: 0,
      selectedOption: null,
      isAnswered: false,
      score: 0,
      userAnswers: [],
      isComplete: false,
    });
  };

  const currentQ: QuizQuestion | undefined = quizState.questions[quizState.currentIndex];

  if (!mounted || quizState.questions.length === 0 || !currentQ) {
    return (
      <main className="quiz-404-container">
        <div className="quiz-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem' }}>
          <div className="quiz-loading-spinner" />
        </div>
      </main>
    );
  }

  const progressPercent = Math.round(
    ((quizState.currentIndex + (quizState.isComplete ? 1 : 0)) / quizState.questions.length) * 100
  );

  const handleOptionSelect = (optionIndex: number) => {
    if (quizState.isAnswered || !currentQ) return;

    const isCorrect = optionIndex === currentQ.correctIndex;
    setQuizState((prev) => ({
      ...prev,
      selectedOption: optionIndex,
      isAnswered: true,
      score: isCorrect ? prev.score + 1 : prev.score,
      userAnswers: [
        ...prev.userAnswers,
        {
          questionId: currentQ.id,
          selectedOption: optionIndex,
          isCorrect,
        },
      ],
    }));
  };

  const handleNextQuestion = () => {
    if (quizState.currentIndex + 1 >= quizState.questions.length) {
      setQuizState((prev) => ({
        ...prev,
        isComplete: true,
      }));
    } else {
      setQuizState((prev) => ({
        ...prev,
        currentIndex: prev.currentIndex + 1,
        selectedOption: null,
        isAnswered: false,
      }));
    }
  };

  const getRankBadge = (score: number) => {
    if (score === 10) return { title: '🏆 Career Mastermind!', desc: 'Flawless 100%! You are a true hiring and resume expert.' };
    if (score >= 8) return { title: '🌟 Career Pro!', desc: 'Outstanding knowledge! You know the workplace inside and out.' };
    if (score >= 5) return { title: '🚀 Career Explorer', desc: 'Good effort! You have solid foundational career smarts.' };
    return { title: '🌱 Career Apprentice', desc: 'A great learning start! Test your knowledge again with 10 new questions.' };
  };

  const isPerfectScore = quizState.score === quizState.questions.length;

  return (
    <main className="quiz-404-wrapper">
      {/* Multiple scattered subtle glass 404 backdrop text elements */}
      <div className="quiz-bg-404-scattered" aria-hidden="true">
        <span className="glass-404-item glass-404-top-center">404</span>
        <span className="glass-404-item glass-404-top-left">404</span>
        <span className="glass-404-item glass-404-top-right">404</span>
        <span className="glass-404-item glass-404-mid-left">404</span>
        <span className="glass-404-item glass-404-mid-right">404</span>
        <span className="glass-404-item glass-404-bottom-left">404</span>
        <span className="glass-404-item glass-404-bottom-right">404</span>
      </div>

      <div className="quiz-404-container">
        {/* Header section with high visual priority for 404 Page Not Found */}
        <header className="quiz-404-header">
          <div className="quiz-404-hero-badge">
            <span className="material-symbols-outlined">error</span>
            <span className="quiz-404-code">404</span>
            <span className="quiz-404-divider">•</span>
            <span className="quiz-404-label">PAGE NOT FOUND</span>
          </div>

          <h1 className="quiz-404-title">
            Oops! Page Not Found <br />
            <span className="gradient-text">Take the 10-Question Career Quiz!</span>
          </h1>

          <p className="quiz-404-subtitle">
            {missingRoute ? (
              <>
                The path <code className="quiz-route-code">{missingRoute}</code> does not exist or has been moved. While you get back on track, test your career &amp; resume knowledge below!
              </>
            ) : (
              'The page you requested was not found. While you navigate back home or to your resume builder, challenge yourself with our 10-question career trivia!'
            )}
          </p>
        </header>

        {/* Quiz Area */}
        {!quizState.isComplete ? (
          <div className="quiz-card">
            {/* Top Bar */}
            <div className="quiz-card-header">
              <div className="quiz-step-info">
                <span className="quiz-step-badge">Question {quizState.currentIndex + 1} of {quizState.questions.length}</span>
                <span className="quiz-category-tag">{currentQ.category}</span>
              </div>
              <div className="quiz-score-live">
                Score: <span className="score-num">{quizState.score}</span>
              </div>
            </div>

            {/* Progress bar */}
            <div className="quiz-progress-bar">
              <div
                className="quiz-progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            {/* Question Title */}
            <h2 className="quiz-question-text">{currentQ.question}</h2>

            {/* Options */}
            <div className="quiz-options-grid">
              {currentQ.options.map((optionText, idx) => {
                let statusClass = '';
                if (quizState.isAnswered) {
                  if (idx === currentQ.correctIndex) {
                    statusClass = 'option-correct';
                  } else if (idx === quizState.selectedOption) {
                    statusClass = 'option-wrong';
                  } else {
                    statusClass = 'option-disabled';
                  }
                }

                const optionLetter = String.fromCharCode(65 + idx);

                return (
                  <button
                    key={idx}
                    type="button"
                    className={`quiz-option-btn ${statusClass}`}
                    onClick={() => handleOptionSelect(idx)}
                    disabled={quizState.isAnswered}
                  >
                    <span className="option-letter">{optionLetter}</span>
                    <span className="option-text">{optionText}</span>
                    {quizState.isAnswered && idx === currentQ.correctIndex && (
                      <span className="option-icon" style={{ color: '#10b981' }}>✓</span>
                    )}
                    {quizState.isAnswered && idx === quizState.selectedOption && idx !== currentQ.correctIndex && (
                      <span className="option-icon" style={{ color: '#ef4444' }}>✕</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Explanation box after answering */}
            {quizState.isAnswered && (
              <div className="quiz-explanation-box">
                <div className="explanation-header">
                  <span className="explanation-title">
                    {quizState.selectedOption === currentQ.correctIndex ? '🎉 Correct!' : '💡 Explanation:'}
                  </span>
                </div>
                <p className="explanation-text">{currentQ.explanation}</p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                  <button
                    type="button"
                    className="quiz-next-btn"
                    onClick={handleNextQuestion}
                  >
                    {quizState.currentIndex + 1 >= quizState.questions.length ? 'See Final Score ➔' : 'Next Question ➔'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Completion Screen with Animated Trophy on 10/10 */
          <div className="quiz-card quiz-results-card">
            <div className="quiz-results-header">
              <div className={`rank-badge-icon ${isPerfectScore ? 'trophy-10-10-wrapper' : ''}`}>
                {isPerfectScore ? (
                  <div className="trophy-10-10-container">
                    <div className="golden-halo-aura" />
                    <span className="sparkle-star s1">✨</span>
                    <span className="sparkle-star s2">🌟</span>
                    <span className="sparkle-star s3">⭐</span>
                    <span className="sparkle-star s4">✨</span>
                    <span className="trophy-emoji">🏆</span>
                  </div>
                ) : (
                  getRankBadge(quizState.score).title.split(' ')[0]
                )}
              </div>
              <h2 className="results-rank-title">{getRankBadge(quizState.score).title}</h2>
              <p className="results-rank-desc">{getRankBadge(quizState.score).desc}</p>
            </div>

            <div className="results-score-box">
              <div className="results-score-big">
                {quizState.score} <span className="results-total">/ {quizState.questions.length}</span>
              </div>
              <p className="results-score-label">Questions Correct</p>
            </div>

            <div className="quiz-actions-grid">
              <button
                type="button"
                className="quiz-action-btn primary-btn"
                onClick={handleStartOver}
              >
                🔄 Play Again (10 New Questions)
              </button>
              <Link href="/" className="quiz-action-btn outline-btn">
                🏠 Back to Home
              </Link>
              <Link href="/account/resume" className="quiz-action-btn outline-btn">
                📝 Resume Builder
              </Link>
            </div>
          </div>
        )}

        {/* Footer shortcuts */}
        <footer className="quiz-404-footer-nav">
          <span className="footer-label">Quick Links:</span>
          <Link href="/" className="footer-link">Home</Link>
          <span className="dot">•</span>
          <Link href="/account/dashboard" className="footer-link">Dashboard</Link>
          <span className="dot">•</span>
          <Link href="/account/resume" className="footer-link">Resumes</Link>
          <span className="dot">•</span>
          <Link href="/guides" className="footer-link">Career Guides</Link>
        </footer>
      </div>
    </main>
  );
}
